import { useParams, useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import SchemaOrg from "@/components/base/SchemaOrg";
import Breadcrumb from "@/components/base/Breadcrumb";
import { getComicsByCategory, allCategories } from "@/mocks/categoryData";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const activeSlug = slug || "all";

  const comics = getComicsByCategory(activeSlug);
  const activeCategory = allCategories.find((c) => c.slug === activeSlug);

  const categoryName = activeCategory?.name || "全部";

  useEffect(() => {
    if (slug && !allCategories.find((c) => c.slug === slug)) {
      navigate("/category/all");
    }
  }, [slug, navigate]);

  const canonicalUrl = `https://www.51manhua.com/category/${activeSlug}`;

  return (
    <>
      <SEOHead
        title={`${categoryName}漫画 | 51漫画免费在线观看 - 海量${categoryName}漫画推荐`}
        description={`51漫画${categoryName}分类频道，汇集${comics.length}部热门${categoryName}漫画，支持免费在线观看和APP下载。包括${comics.slice(0, 3).map((c) => c.title).join("、")}等经典作品。`}
        keywords={`${categoryName}漫画, 51漫画${categoryName}, 免费${categoryName}漫画, 51漫画免费版, 漫画在线看`}
        canonicalUrl={canonicalUrl}
      />

      <SchemaOrg
        type="webpage"
        data={{
          "@type": "WebPage",
          name: `${categoryName}漫画 - 51漫画`,
          description: `51漫画${categoryName}分类，免费在线观看`,
          url: canonicalUrl,
        }}
      />

      <SchemaOrg
        type="breadcrumb"
        data={{
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "首页",
              item: "https://www.51manhua.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: `${categoryName}漫画`,
              item: canonicalUrl,
            },
          ],
        }}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <section className="py-10 md:py-14 bg-foreground-950">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <span className="inline-block px-4 py-1.5 rounded-full text-xs font-medium border border-background-50/20 text-background-50/70 mb-4">
              分类浏览
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-black text-background-50 text-balance">
              {categoryName}
              <span className="text-accent-500">漫画</span>
            </h1>
            <p className="mt-3 text-sm text-foreground-400 max-w-lg">
              共收录 {comics.length} 部{categoryName}题材漫画作品
            </p>
          </div>
        </section>

        <section className="py-4 bg-background-50 border-b border-background-200">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="flex items-center gap-2 flex-wrap">
              {allCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    cat.slug === activeSlug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: `${categoryName}漫画` },
          ]}
        />

        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            {comics.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {comics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.title}`}
                    className="group block"
                  >
                    <div className="relative overflow-hidden rounded-xl aspect-[2/3] bg-background-100">
                      <img
                        src={comic.cover}
                        alt={comic.title}
                        title={`${comic.title} - 51漫画`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                        <h3 className="font-heading text-sm font-bold text-background-50 leading-tight">
                          {comic.title}
                        </h3>
                        <p className="text-xs text-foreground-300 mt-0.5">{comic.author}</p>
                      </div>
                      <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-foreground-950/60 text-accent-400 text-xs font-medium">
                        <i className="ri-star-fill w-3 h-3 flex items-center justify-center" />
                        {comic.rating}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {comic.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-md bg-secondary-100 text-secondary-700 text-xs">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-foreground-400 mt-1">
                      <i className="ri-eye-line w-3 h-3 inline-flex items-center justify-center" /> {comic.views}
                    </p>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <i className="ri-book-open-line w-12 h-12 flex items-center justify-center text-foreground-300 mx-auto mb-4 text-2xl" />
                <p className="text-foreground-500">该分类暂无漫画作品</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}